import { type AppDispatch, type AppThunk } from "features/Application/globaltypes/redux";
import { pluralize } from "utils/stringUtils";
import { tagEntity } from "components/addTagsModal/helpers";
import { type NotifyStepSettings, type UpdateParams, type DistributedOpUpdateParams } from "interfaces";
import backgroundTask from "features/BackgroundTasks/backgroundTask";
import { TagsEnum } from "interfaces/TagsEnum";
import midnightService from "features/Application/services/midnightService/midnightService";
import DraftEntityTypes from "enums/draftEntityTypes";
import {
  LinkDiscardSuccess,
  LinkLockSuccess,
  LinkPublishSuccess,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import { beginAsyncOperation } from "features/Application/slices/asyncOperationSlice";
import {
  beginAction,
  createDraftSuccess,
  failureAction,
  successAction,
  updateBegin,
  updateSuccess,
} from "../ExternalLinkEntityStateSlice";
import { type ExternalLinkFormData } from "../../types/externalLinkTypes";
import { DataService } from "features/Application/services/dataServices/typedDataService";
import type { BasePerformanceRequestFilterParams } from "features/Library/Common/models";
import { sendExportRequest } from "features/Library/Common/utils/performanceUtils";
import { getLinkExport } from "features/Reporting/services/contentReportingService";

export const createExternalLinkThunk =
  (payload: ExternalLinkFormData): AppThunk =>
  async (dispatch) => {
    dispatch(beginAction());
    try {
      const { data } = await DataService.links.createExternalLink(payload);
      dispatch(createDraftSuccess({ entityId: data }));
    } catch (error: any) {
      dispatch(failureAction({ entityId: 0 }));
    }
  };

export const updateExternalLinkThunk =
  (payload: ExternalLinkFormData): AppThunk<Promise<ExternalLinkFormData | null>> =>
  async (dispatch) => {
    if (!payload.id) {
      dispatch(failureAction({ entityId: 0 }));
      return null;
    }
    dispatch(updateBegin());
    try {
      await DataService.links.updateExternalLink(payload.id, payload);
      dispatch(updateSuccess({ entityId: payload.id }));
      return payload;
    } catch (error: any) {
      dispatch(failureAction({ error, entityId: payload.id }));
      return null;
    }
  };

export const publishExternalLink =
  (id: number, notificationSettings?: NotifyStepSettings, notifyTypes?: number[]) => async (dispatch: AppDispatch) => {
    dispatch(beginAction());
    dispatch(beginAsyncOperation({ id, action: LinkPublishSuccess }));
    try {
      await midnightService.releaseLock(
        DraftEntityTypes.Links,
        id,
        notificationSettings ? { notificationSettings, notifyTypes } : undefined,
      );
    } catch (error) {
      dispatch(failureAction({ error }));
    }
  };

export const addTagsToExternalLink =
  (ids: number[], tags: string[], entityType: TagsEnum): AppThunk =>
  async (dispatch: AppDispatch) => {
    if (!tags.length) {
      return;
    }
    const getMessageIds = (pdfIds: number[], _tags?: string[]) => async () => {
      const result = await DataService.links.addTagsToExternalLink({ ids: pdfIds, tags, tagType: entityType });
      return result?.data.messageIds;
    };

    const transientMessagePrefix = pluralize(tagEntity[entityType], tags.length);
    const updateParams: UpdateParams = {
      id: "AddTagsToLinkAsset",
      title: `${transientMessagePrefix} adding`,
      getMessageIds: getMessageIds(ids),
      successTransientMessage: `${transientMessagePrefix} ${tags.length === 1 ? "has" : "have"} been added!`,
      failureTransientMessage: `${transientMessagePrefix} add failed!`,
    };

    await backgroundTask.initializeBackgroundTask(updateParams, dispatch);
  };

export const updateTagsOnExternalLink = (id: number, tags: string[], type: "labels" | "softwareApplications") => {
  const tagTypeMap = {
    labels: TagsEnum.Label,
    softwareApplications: TagsEnum.SoftwareApplication,
  };

  return async () => {
    try {
      await DataService.links.updateExternalLinkTags(id, { tags, tagType: tagTypeMap[type] });
    } catch (error) {}
  };
};

export const fetchDraftExternalLinkEntity = (id: number) => async (dispatch: AppDispatch) => {
  try {
    dispatch(beginAction());
    dispatch(beginAsyncOperation({ id, action: LinkLockSuccess }));
    await midnightService.getEntityLock(DraftEntityTypes.Links, id);
  } catch (error) {
    dispatch(failureAction({ error }));
  }
};

export const deleteExternalLinks =
  (ids: number[], refetchExternalLinks: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    const getOperationProps = async () => {
      const { data } = await DataService.links.deleteExternalLinks(ids);
      return data;
    };

    const idsCount = ids.length;
    const entity = pluralize("Link", idsCount);
    const updateParams: DistributedOpUpdateParams = {
      id: "DeleteExternalLinks",
      title: `Deletion of ${entity}`,
      onCompleted: () => refetchExternalLinks(),
      getOperationProps,
      successTransientMessage: `${entity} ${idsCount === 1 ? "has" : "have"} been deleted!`,
      failureTransientMessage: `${entity} delete failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };

export const lockExternalLink =
  (id: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(beginAction());
    dispatch(beginAsyncOperation({ id, action: LinkLockSuccess }));
    try {
      await midnightService.getEntityLock(DraftEntityTypes.Links, id);
      successAction({ entityId: id });
    } catch (error) {
      dispatch(failureAction({ error }));
    }
  };

export const discardExternalLink =
  (id: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(beginAction());
    dispatch(beginAsyncOperation({ id, action: LinkDiscardSuccess }));
    try {
      await midnightService.discardLock(DraftEntityTypes.Links, id);
    } catch (error) {
      dispatch(failureAction({ error }));
    }
  };

export const changeExternalLinkVisibility =
  (ids: number[], hide: boolean, onCompleted: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    const idsCount = ids.length;
    const entity = pluralize("Link", idsCount);

    const updateParams: DistributedOpUpdateParams = {
      id: "VisibilityLinkDistributedOperation",
      title: `${hide ? "Showing" : "Hiding"} ${entity}`,
      onCompleted,
      getOperationProps: async () => {
        const { data } = await DataService.links.changeExternalLinkVisibility(ids, hide);
        return data;
      },
      successTransientMessage: `${entity} ${idsCount === 1 ? "has" : "have"} been ${hide ? "shown" : "hidden"} successfully!`,
      failureTransientMessage: `${entity} ${hide ? "showing" : "hiding"} failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };

export const duplicateExternalLinks =
  (ids: number[], fetchExternalLinks: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    const getOperationProps = async () => {
      const { data } = await DataService.links.duplicateExternalLinks(ids);
      return data;
    };

    const idsCount = ids.length;
    const bannerPrefix = pluralize("Link", idsCount);

    const updateParams: DistributedOpUpdateParams = {
      id: "DuplicateExternalLinks",
      title: `Duplication of ${bannerPrefix}`,
      onCompleted: () => fetchExternalLinks(),
      getOperationProps,
      successTransientMessage: `${bannerPrefix} ${idsCount === 1 ? "has" : "have"} been duplicated!`,
      failureTransientMessage: `${bannerPrefix} duplicate failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };

let abortController = new AbortController();

export const doLinkExport = (filterParams: BasePerformanceRequestFilterParams, name: string) => {
  return sendExportRequest((abortController) => getLinkExport(filterParams, abortController), name, abortController);
};
